import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { graphql, withApollo } from 'react-apollo';
import { compose, mapProps } from 'recompose';
import PropTypes from 'prop-types';

import {
  updateValuesListFieldValue,
  userStartTyping as userStartTypingMut,
  userStopTyping as userStopTypingMut,
  getLatestAssessmentEventById,
  ddeActionsTriggered
} from 'graphql/operations';
import { NO_CACHE } from 'graphql/fetchPolicies';
import { getObjValue } from 'utils/functions';
import { useDebouncedStartTyping, useDebouncedStopTyping } from 'components/threepp/assessment/Answers/helper';
import { createSelectionValidationFn } from 'utils/validations/validations';
import { decodeHtml, encodeHtml } from 'utils/htmlEncoderDecoder';
import { AutocompleteDropdown } from 'components/common/Dropdown/AutocompleteDropdown';
import { OTHER_TEXT_MAX_CHARACTERS } from 'utils/constants';
import SaveAnswerHOC from '../SaveAnswerHOC';
import EventHandlerHOC from '../FieldUpdatesHOC';

export const DropdownAnswer = props => {
  const { t } = useTranslation();
  const { question, disabled, save, filterValues, multiple, latestEvent, userStartTyping, assessmentId, userStopTyping } = props;
  const { listValue, minimumSelection, maximumSelection, calculated, possibleValues } = question;

  const action = getObjValue(latestEvent, 'action');
  const isDisabled = disabled || action === 'START_TYPING';

  const userStartTypingCallback = useDebouncedStartTyping(userStartTyping, assessmentId, question.id);
  const userStopTypingCallback = useDebouncedStopTyping(userStopTyping, assessmentId, question.id);

  // Local Vars
  let options = possibleValues.map(valuesListField => {
    const { valuesListId, value, enableOtherText, defaultOtherText } = valuesListField;
    return { value: valuesListId, label: decodeHtml(value), enableOtherText, defaultOtherText: decodeHtml(defaultOtherText) };
  });
  if (filterValues && filterValues.length > 0) {
    options = options.filter(option => filterValues.indexOf(option.value) > -1);
  }

  // Event Handlers
  const handleValueChange = React.useCallback(
    (selectedValues, otherText) => {
      userStartTypingCallback();
      userStopTypingCallback();
      save(selectedValues, encodeHtml(otherText) || null);
    },
    [save, userStartTypingCallback, userStopTypingCallback]
  );

  const inputValidation = createSelectionValidationFn(minimumSelection, maximumSelection);

  const handleValidationError = React.useCallback(
    (selectedValues, otherText) => {
      save(selectedValues, encodeHtml(otherText) || null);
    },
    [save]
  );

  const errorObj = listValue && listValue.length > 0 ? inputValidation(listValue.length) : null;

  return (
    <AutocompleteDropdown
      t={t}
      id={question.id}
      options={options}
      disabled={calculated || isDisabled}
      selected={listValue}
      onChange={handleValueChange}
      inputValidation={inputValidation}
      onValidationError={handleValidationError}
      error={errorObj ? t(errorObj.key, { ...errorObj.args }) : null}
      otherText={decodeHtml(question.otherText)}
      multiple={multiple}
      otherTextMaxCharacters={OTHER_TEXT_MAX_CHARACTERS}
    />
  );
};

DropdownAnswer.defaultProps = {
  disabled: false,
  latestEvent: { action: '' }
};

DropdownAnswer.propTypes = {
  disabled: PropTypes.bool,
  // TODO: Fix this...
  /* eslint-disable react/forbid-prop-types */
  save: PropTypes.any.isRequired,
  question: PropTypes.object.isRequired,
  userStartTyping: PropTypes.func.isRequired,
  userStopTyping: PropTypes.func.isRequired,
  latestEvent: PropTypes.object
  /* eslint-enable react/forbid-prop-types */
};

export const DropdownAnswerHOC = compose(
  withApollo,
  graphql(updateValuesListFieldValue, { name: 'fieldMutation' }),
  graphql(userStartTypingMut, { name: 'userStartTyping' }),
  graphql(userStopTypingMut, { name: 'userStopTyping' }),
  graphql(ddeActionsTriggered, { name: 'ddeActionsTriggered' }),
  graphql(getLatestAssessmentEventById, {
    options: props => ({
      variables: {
        fieldId: props.question.id
      },
      fetchPolicy: NO_CACHE
    }),
    name: 'getLatestAssessmentEventById'
  }),
  EventHandlerHOC,
  SaveAnswerHOC,
  mapProps(props => ({
    question: props.question,
    assessmentId: props.assessmentId,
    save: props.save,
    disabled: props.disabled,
    filterValues: props.filterValues,
    invokeActionsForFields: props.invokeActionsForFields,
    userStartTyping: props.userStartTyping,
    userStopTyping: props.userStopTyping,
    SVLAction: props.SVLAction,
    latestEvent: props.getLatestAssessmentEventById.getLatestAssessmentEventById,
    multiple: props.multiple
  }))
);

export default DropdownAnswerHOC(DropdownAnswer);
